import * as _ from 'lodash';
import api from 'utils/api';
import { cloneDeep } from 'utils/immutable';
import { generateColorScaleFromHex } from 'utils/color';
import { produce } from 'immer';
import constants from 'utils/constants';
import { logout } from 'ducks/modules/user';
import Initialiser from 'initialiser';
import store from 'ducks/configureStore';

// ACTION TYPES
const START_FETCH_WORK_DETAILS = 'START_FETCH_WORK_DETAILS';
const UPDATED_WORK_DETAILS = 'UPDATED_WORK_DETAILS';
const START_FETCH_WORK_METADATA = 'START_FETCH_WORK_METADATA';
const UPDATED_WORK_METADATA = 'UPDATED_WORK_METADATA';
const START_FETCH_WORK_RECORDINGS = 'START_FETCH_WORK_RECORDINGS';
const UPDATED_WORK_RECORDINGS = 'UPDATED_WORK_RECORDINGS';
const START_FETCH_WORK_ACTIVITY = 'START_FETCH_WORK_ACTIVITY';
const UPDATED_WORK_ACTIVITY = 'UPDATED_WORK_ACTIVITY';
// const RESET_WORK = 'RESET_WORK';

// SYNC ACTION CREATORS
export function startFetchWorkDetails() {
    return { type: START_FETCH_WORK_DETAILS };
}
export function updatedWorkDetails(work) {
    return { type: UPDATED_WORK_DETAILS, work };
}
export function startFetchWorkMetadata() {
    return { type: START_FETCH_WORK_METADATA };
}
export function updatedWorkMetadata(metadata) {
    return { type: UPDATED_WORK_METADATA, metadata };
}
export function startFetchWorkRecordings() {
    return { type: START_FETCH_WORK_RECORDINGS };
}
export function updatedWorkRecordings(recordings) {
    return { type: UPDATED_WORK_RECORDINGS, recordings };
}
export function startFetchWorkActivity() {
    return { type: START_FETCH_WORK_ACTIVITY };
}
export function updatedWorkActivity(activity) {
    return { type: UPDATED_WORK_ACTIVITY, activity };
}

// ASYNC ACTION CREATORS
export function fetchWorkDetails(bkWorkId, country) {
    return async (dispatch) => {
        dispatch(startFetchWorkDetails());
        try {
            const work = await api.fetchWorkDetails(bkWorkId);
            work.claims = await api.fetchWorkClaims(bkWorkId, country);

            const mergedDetails = produce(work, (draft) => {
                // Transform activity items for grouping
                draft.granularActivity = work.activity;
                const granularActivity = work.activity.items;

                // Attach shares information inside a writer
                granularActivity.map((activity) => {
                    if (activity.share) {
                        if (activity.writer) {
                            activity.writer = _.merge(activity.writer, activity.share);
                        } else if (activity.publisher) {
                            activity.publisher = _.merge(activity.publisher, activity.share);
                        }
                    }
                    return activity;
                });

                // First grouping by group id for grouping countries
                const groupedActivity = _(granularActivity)
                    .filter((item) => item.group_id)
                    .groupBy('group_id')
                    .map((values) => ({ ...values[0], countries: _(values).map('country').uniqBy('code').value() }))
                    .value();

                // Attach list of countries to specific writer entities
                groupedActivity.map((activity) => {
                    if (activity.countries) {
                        if (activity.writer) {
                            activity.writer.countries = activity.countries;
                        } else if (activity.publisher) {
                            activity.publisher.countries = activity.countries;
                        }
                    }
                    return activity;
                });

                // Merge grouped and ungrouped items
                const mergedActivities = granularActivity.filter((item) => !item.group_id).concat(groupedActivity);

                // Second grouping to cluster writers/publishers that were added on the same ingestion
                const aggregatedActivities = _(mergedActivities)
                    .groupBy('group_cluster_id')
                    .map((values) => ({
                        ...values[0],
                        writers: _(values).map('writer').uniqBy('ipi').value(),
                        publishers: _(values).map('publisher').uniqBy('ipi').value(),
                    }))
                    .value();

                // Replace the countries to include all unique countries aggregated in the group
                aggregatedActivities.map((activity) => {
                    const allCountries = [];
                    if (activity.writer) {
                        for (const writer of activity.writers) {
                            allCountries.push(writer.countries);
                        }
                    } else if (activity.publisher) {
                        for (const publisher of activity.publishers) {
                            allCountries.push(publisher.countries);
                        }
                    }
                    activity.countries = _.uniqBy(allCountries.flat(), 'code');
                    return activity;
                });

                draft.activity.items = _.orderBy(aggregatedActivities, 'group_id', 'desc');
            });

            dispatch(updatedWorkDetails(mergedDetails));
        } catch (err) {
            console.error('Error fetching work details: ', err);
            dispatch(updatedWorkDetails({}));
            if (err) {
                switch (err.status) {
                    case constants.HttpCodes.UNAUTHORIZED:
                        return dispatch(logout(err.data === constants.ServerCodes.TOKEN_EXPIRED));
                    case constants.HttpCodes.FORBIDDEN:
                        return Initialiser.history.replace('/forbidden');
                    case constants.HttpCodes.BAD_REQUEST:
                    case constants.HttpCodes.NOT_FOUND:
                        return Initialiser.history.replace('/notfound');
                    case constants.HttpCodes.INTERNAL_SERVER_ERROR:
                        return Initialiser.history.replace('/error');
                }
            }
        }
    };
}

export function fetchWorkMetadata(bkWorkId, country) {
    return async (dispatch) => {
        dispatch(startFetchWorkMetadata());
        dispatch(startFetchWorkRecordings());
        try {
            const metadata = await api.fetchWorkMetadata(bkWorkId);
            const claims = await api.fetchWorkClaims(bkWorkId, country);
            metadata.claims = claims;
            dispatch(updatedWorkMetadata(metadata));
        } catch (err) {
            console.error('Error fetching work metadata: ', err);
            dispatch(updatedWorkMetadata({}));
            if (err) {
                switch (err.status) {
                    case constants.HttpCodes.UNAUTHORIZED:
                        return dispatch(logout(err.data === constants.ServerCodes.TOKEN_EXPIRED));
                    case constants.HttpCodes.FORBIDDEN:
                        return Initialiser.history.replace('/forbidden');
                    case constants.HttpCodes.BAD_REQUEST:
                    case constants.HttpCodes.NOT_FOUND:
                        return Initialiser.history.replace('/notfound');
                    case constants.HttpCodes.INTERNAL_SERVER_ERROR:
                        return Initialiser.history.replace('/error');
                }
            }
        }
    };
}

export function fetchWorkRecordings(bkWorkId) {
    return async (dispatch) => {
        dispatch(startFetchWorkRecordings());
        try {
            const recordings = await api.fetchWorkRecordings(bkWorkId);
            dispatch(updatedWorkRecordings(recordings));
        } catch (err) {
            console.error('Error fetching work recordings: ', err);
            dispatch(updatedWorkRecordings([]));
            if (err) {
                switch (err.status) {
                    case constants.HttpCodes.UNAUTHORIZED:
                        return dispatch(logout(err.data === constants.ServerCodes.TOKEN_EXPIRED));
                    case constants.HttpCodes.FORBIDDEN:
                        return Initialiser.history.replace('/forbidden');
                    case constants.HttpCodes.BAD_REQUEST:
                    case constants.HttpCodes.NOT_FOUND:
                        return Initialiser.history.replace('/notfound');
                }
            }
        }
    };
}

export function fetchWorkActivity(bkWorkId, offset, limit) {
    return async (dispatch) => {
        dispatch(startFetchWorkActivity());
        try {
            const activity = await api.fetchWorkActivity(bkWorkId, offset, limit);
            dispatch(updatedWorkActivity(activity));
        } catch (err) {
            console.error('Error fetching work activity: ', err);
        }
    };
}

// UTILS
const colors = {
    writers: {
        start: '#6edfdb',
        end: '#71d9f8',
    },
    publishers: {
        start: '#80bfff',
        end: '#71d9f8',
    },
};

function populateWithGradient(shares) {
    for (const territory of Object.keys(shares)) {
        const colorsWriters = generateColorScaleFromHex(
            colors.writers.start,
            colors.writers.end,
            shares[territory].writer_shares.length,
        );
        for (let i = 0; i < shares[territory].writer_shares.length; i++) {
            // Add color
            shares[territory].writer_shares[i].color = '#' + colorsWriters[i];
        }
        const colorsPublishers = generateColorScaleFromHex(
            colors.publishers.start,
            colors.publishers.end,
            shares[territory].publisher_shares.length,
        );
        for (let i = 0; i < shares[territory].publisher_shares.length; i++) {
            shares[territory].publisher_shares[i].color = '#' + colorsPublishers[i];
        }
    }
}

function attachMechanicalSharesToWriters(writerShares, publisherShares) {
    const newWriterShares = cloneDeep(writerShares);
    newWriterShares.map((writer) => {
        if (!writer.hasOwnProperty('mr_share_split')) {
            writer.mr_share_split = {};
        }
        // For linked writers sum up mechanical shares from their linked publishers
        publisherShares.map((publisher) => {
            const granularWriterShares = publisher.mr_share;
            if (publisher.link && publisher.link.includes(writer.rollup_ipi.toString())) {
                if (writer.mr_share_split.hasOwnProperty(publisher.ipi)) {
                    writer.mr_share_split[`${publisher.ipi}`] =
                        writer.mr_share_split[publisher.ipi] + granularWriterShares;
                }
                if (!writer.mr_share_split.hasOwnProperty(publisher.ipi)) {
                    writer.mr_share_split[`${publisher.ipi}`] = granularWriterShares;
                }
            }
            return publisher;
        });
        // For unlinked writers in state, display 2x the performance share value for that writer in the table (in mech view)
        if (writer.hasOwnProperty('mr_share_split') && _.isEmpty(writer.mr_share_split) && _.isEmpty(writer.link)) {
            writer.mr_share_split.inferred = writer.pr_share * 2;
        }
        return writer;
    });

    return newWriterShares;
}

// We now (11/10/19) aggregate shares together by common ipi
function aggregateShares(shares) {
    const reducer = (aggregatedShares, share, index, originalShares) => {
        // All negative IPIs, almost always associated with uncontrolled
        // entities, are defaulted to 0 to avoid exposing our internal
        // identifiers (useless to the user).
        // Therefore we must avoid aggregation on entities with IPI 0
        if (Number(share.ipi) === 0) {
            aggregatedShares.push(share);
            return aggregatedShares;
        }
        const i = _.findIndex(
            aggregatedShares,
            (s) =>
                // We now (11/10/19) aggregate shares when they match on ipi AND have the same conflict status
                s.ipi === share.ipi &&
                s.hasOwnProperty('conflictsWith') === share.hasOwnProperty('conflictsWith') &&
                s.source_controls === share.source_controls,
        );
        if (i > -1) {
            // Aggregate but remove duplicates and keep order (NOTE: This is not optimised!)
            aggregatedShares[i].chain_of_title = _.sortBy(
                aggregatedShares[i].chain_of_title
                    .concat(share.chain_of_title)
                    .filter((el, i, a) => i === a.indexOf(el)),
            );
            // Aggregate shares
            aggregatedShares[i].mr_share += share.mr_share;
            aggregatedShares[i].pr_share += share.pr_share;

            // Aggregate links
            aggregatedShares[i].link = _.uniq((aggregatedShares[i].link || []).concat(share.link || []));
            if (!aggregatedShares[i].hasOwnProperty('original_ipis')) {
                aggregatedShares[i].original_ipis = [];
            }

            aggregatedShares[i].original_ipis.push(share.original_ipi);
            aggregatedShares[i].original_ipis = _.uniq(aggregatedShares[i].original_ipis);

            // TODO: There should not be any need to do this
            if (aggregatedShares[i].pr_dispute !== undefined && share.pr_dispute !== undefined) {
                aggregatedShares[i].mr_dispute += share.mr_dispute;
                aggregatedShares[i].pr_dispute += share.pr_dispute;
            }

            // take the meta data from the share who's ipi is the rollup (if available),
            // otherwise the from the first share that we came across
            const rollupShare = originalShares.filter(
                (s) => s.ipi === s.rollup_ipi && s.rollup_ipi === share.rollup_ipi,
            );
            if (rollupShare && rollupShare.length > 0) {
                aggregatedShares[i].rollup_ipi = rollupShare[0].rollup_ipi;
                aggregatedShares[i].mr_society = rollupShare[0].mr_society;
                aggregatedShares[i].pr_society = rollupShare[0].pr_society;
                aggregatedShares[i].last_updated = rollupShare[0].last_updated;

                // DEPRECATED - not aggregating link based on rollup share anymore, using collecting
                // link info is a combination of all links from all aggregated shares (writers could have the link set to null)

                // if (rollupShare[0].hasOwnProperty('link') && rollupShare[0].link !== null) {
                //     if (!aggregatedShares[i].link || aggregatedShares[i].link.length === 0) {
                //         aggregatedShares[i].link = rollupShare[0].link.slice();
                //     } else {
                //         for (const ipi of rollupShare[0].link) {
                //             if (aggregatedShares[i].link.indexOf(ipi) === -1) {
                //                 aggregatedShares[i].link.push(ipi);
                //             }
                //         }
                //     }
                // }

                // conflictsWith info is a combination of conflictsWith from all aggregated shares
                if (aggregatedShares[i].hasOwnProperty('conflictsWith')) {
                    if (!aggregatedShares[i].conflictsWith.hasOwnProperty('pr')) {
                        aggregatedShares[i].conflictsWith.pr = [];
                    }
                    if (!aggregatedShares[i].conflictsWith.hasOwnProperty('mr')) {
                        aggregatedShares[i].conflictsWith.mr = [];
                    }

                    // include any ipis in the conflictsWith from the rollup share too
                    if (
                        rollupShare[0].hasOwnProperty('conflictsWith') &&
                        rollupShare[0].conflictsWith.pr &&
                        rollupShare[0].conflictsWith.pr.length > 0
                    ) {
                        aggregatedShares[i].conflictsWith.pr.push(...rollupShare[0].conflictsWith.pr);
                        aggregatedShares[i].conflictsWith.pr = _.uniq(aggregatedShares[i].conflictsWith.pr);
                    }
                    if (
                        rollupShare[0].hasOwnProperty('conflictsWith') &&
                        rollupShare[0].conflictsWith.mr &&
                        rollupShare[0].conflictsWith.mr.length > 0
                    ) {
                        aggregatedShares[i].conflictsWith.mr.push(...rollupShare[0].conflictsWith.mr);
                        aggregatedShares[i].conflictsWith.mr = _.uniq(aggregatedShares[i].conflictsWith.mr);
                    }
                }
            }
            return aggregatedShares;
        }
        aggregatedShares.push(share);
        return aggregatedShares;
    };
    return shares.reduce(reducer, []);
}

// SELECTORS
export function selectWork() {
    return store.getState().work;
}

export function isMLCWork() {
    const workState = selectWork();
    return _.get(workState, 'metadata.bk_work_id') < 0;
}

export function hasEmptyActivity() {
    const workState = selectWork();
    return _.isEmpty(workState, 'activity.items');
}

// REDUCER
export default function reducer(state = Initialiser.initialState.work, action) {
    switch (action.type) {
        case UPDATED_WORK_DETAILS: {
            const work = produce(action.work, (work) => {
                work.metadata = state.metadata;

                if (work.state) {
                    populateWithGradient(work.state);
                }
                if (work.source) {
                    populateWithGradient(work.source);
                }

                const aggregate = (work) => {
                    if (work.hasOwnProperty('source')) {
                        for (const country in work.source) {
                            work.source[country].writer_shares = attachMechanicalSharesToWriters(
                                work.source[country].writer_shares,
                                work.source[country].publisher_shares,
                            );
                            work.source[country].writer_shares = aggregateShares(work.source[country].writer_shares);
                            work.source[country].publisher_shares = aggregateShares(
                                work.source[country].publisher_shares,
                            );
                        }
                    }
                    if (work.hasOwnProperty('state')) {
                        for (const country in work.state) {
                            work.state[country].writer_shares = attachMechanicalSharesToWriters(
                                work.state[country].writer_shares,
                                work.state[country].publisher_shares,
                            );
                            work.state[country].writer_shares = aggregateShares(work.state[country].writer_shares);
                            work.state[country].publisher_shares = aggregateShares(
                                work.state[country].publisher_shares,
                            );
                        }
                    }
                };
                aggregate(work);
                for (const claim of work.claims || []) {
                    if (claim.hasOwnProperty('claimantWork')) {
                        aggregate(claim.claimantWork);
                    }
                }
            });

            return work;
        }
        case UPDATED_WORK_METADATA: {
            const work = produce(state, (draft) => {
                draft.metadata = { ...action.metadata, linked_recordings: state.metadata.linked_recordings || [] };
            });

            return work;
        }

        case UPDATED_WORK_RECORDINGS: {
            const work = produce(state, (draft) => {
                const sortedRecordings = _(action.recordings)
                    .map((recording) => ({
                        ...recording,
                        popularity:
                            (recording.metadata.spotify && recording.metadata.spotify.popularity) ||
                            (recording.metadata.deezer && recording.metadata.deezer.popularity) ||
                            (recording.metadata.source && recording.metadata.source.popularity) ||
                            0,
                    }))
                    .orderBy('popularity', 'desc')
                    .value();
                draft.metadata.linked_recordings = sortedRecordings;
            });
            return work;
        }
        case UPDATED_WORK_ACTIVITY: {
            const work = produce(state, (draft) => {
                draft.activity.items = state.activity.items.concat(action.activity.items);
                draft.activity.nextOffset = action.activity.nextOffset;
            });

            return work;
        }

        default: {
            return state;
        }
    }
}
